import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../../../../store";
import { storedLocale } from "../../../../Authentication/util";
import { useLazyAboEnrichment } from "../../../hooks/useLazyAboEnrichment";
import { useWatchlistToggle } from "../../../hooks/useWatchlistToggle";
import { useLazyProfileImages } from "../../../hooks/useLazyProfileImages";
import { useLazyWhatsAppEnrichment } from "../../../hooks/useLazyWhatsAppEnrichment";
import { MobileCard } from "../../MobileCard/MobileCard";
import { getKpiValue } from "../../KPI";
import { useNoDataAvailableLabel } from "../../../../../common/hooks/useNoDataAvailableLabel";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { ActionReportListProps, ActionReportRow, FieldConfig } from "../types";
import { useActionReportList } from "../../../hooks/useActionReportList";
import { getAffAboId, isChinaAff, scrollClearOfStickyNav } from "../utils/actionReportUtils";
import { Virtuoso } from "react-virtuoso";
import { CUSTOMER_BUSINESS_NATURES } from "../../../../../common/enums/businessNatures";
import { useVirtuosoScrollToIndex } from "../../../hooks/useVirtuosoScrollToIndex";
import LeafCircleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafCircleLoader/LeafCircleLoader";
import LeafToastMessage from "../../../../../common/leaf/LeafToastMessage/LeafToastMessage";
import { useTranslation } from "react-i18next";
import watchlistContent from "../../../../../content/watchlistNotifications";

type MobileTableRowProps = {
  index: number;
  row: ActionReportRow;
  selectedKpis: string[];
  kpisToFetch: string[];
  reportId: string;
  period: string;
  fieldConfig: FieldConfig[];
  skipEnrichment?: boolean;
  enrichWhatsApp?: boolean;
  awaitAutoEnrichment?: boolean;
  showABOProfile: (event: CustomEventInit) => void;
  batchSize?: number;
  enableWatchlist?: boolean;
  isSelected?: boolean;
  rowInViewRef?: React.RefObject<{ index: number; timestamp: number } | null>;
  noDataAvailableLabel: string;
  trackABONameClick?: Function;
  onProfileTrack?: Function;
  onWatchlistTrack?: Function;
  onPhoneTrack?: () => void;
  onMessageTrack?: () => void;
  onWhatsAppTrack?: () => void;
  showImageBlock?: boolean;
  mobileCardStyle?: "standard" | "no-divider";
  showTagForDeepCustomers?: boolean;
  onWatchlistNotification?: (name: string, wasInWatchlist: boolean, success: boolean) => void;
};

export const ActionReportMobileCardLoader = ({ numRows = 5 }: { numRows?: number }) => {
  return (
    <div className="arMobileCard">
      <div className="arProfileSection arProfileSection--mobile">
        <div className="arProfileSection__topRow">
          <div className="arProfileSection__badgeAndPhoto">
            <div className="arProfileSection__dashedLine" aria-hidden="true">
              <span className="arProfileSection__dash arProfileSection__dash--loading" />
              <span className="arProfileSection__dash arProfileSection__dash--loading" />
              <span className="arProfileSection__dash arProfileSection__dash--loading" />
              <span className="arProfileSection__dash arProfileSection__dash--loading" />
            </div>

            <div className="arProfileSection__rankBadge arProfileSection__rankBadge--loading"></div>

            <div className={`arProfileSection__photo`} style={{ marginLeft: "1rem" }}>
              <LeafCircleLoader isLoading width="3.25rem" height="3.25rem" />
            </div>
          </div>
          <div className="arProfileSection__nameCol">
            <LeafRectangleLoader isLoading width="9.375rem" height="1rem" margin=".5rem 0 0 0" />
            <LeafRectangleLoader isLoading width="5rem" height="1rem" margin="0 0 1rem 0" />
          </div>
        </div>
        <div className="arProfileSection__actions">
          <LeafCircleLoader isLoading width="2.25rem" height="2.25rem" />
          <LeafCircleLoader isLoading width="2.25rem" height="2.25rem" />
          <LeafCircleLoader isLoading width="2.25rem" height="2.25rem" />
        </div>
      </div>

      <section className="arMobileCard__kpiList">
        {new Array(numRows).fill("x").map((_, index) => (
          <div className="arMobileCard__kpiItem" key={`arMobileCard__kpiItemLoader-${index}`}>
            <LeafRectangleLoader isLoading width="6.25rem" height="1rem" margin="0" />
            <LeafRectangleLoader isLoading width="3.125rem" height="1rem" margin="0" />
          </div>
        ))}
      </section>
    </div>
  );
};

const MobileCardRow = ({
  index,
  row,
  selectedKpis,
  kpisToFetch,
  enableWatchlist = false,
  reportId,
  period,
  batchSize,
  fieldConfig,
  showABOProfile,
  skipEnrichment = false,
  enrichWhatsApp = false,
  awaitAutoEnrichment = false,
  isSelected = false,
  rowInViewRef,
  noDataAvailableLabel,
  trackABONameClick = () => {},
  onProfileTrack = () => {},
  onWatchlistTrack = () => {},
  onPhoneTrack,
  onMessageTrack,
  onWhatsAppTrack,
  showImageBlock = false,
  mobileCardStyle = "standard",
  showTagForDeepCustomers = false,
  onWatchlistNotification,
}: MobileTableRowProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { user } = useAppSelector((state) => state.boot);

  const { isInWatchlist, toggleWatchlist } = useWatchlistToggle({ row });

  const { getEnrichedAbo, getFetchState } = useLazyAboEnrichment({
    reportId,
    period,
    kpis: kpisToFetch,
    locale: storedLocale(),
    isoCountryCode: user.isoCountryCode || "US",
    batchSize,
    skipEnrichment,
  });

  const affAboId = getAffAboId(row);
  const isChina = isChinaAff(row.aff);
  const fetchState = affAboId ? getFetchState(affAboId) : "unfetched";
  const enrichedData = affAboId ? getEnrichedAbo(affAboId) : null;

  // Profile image from lazy-loaded store
  const { getProfileImage, fetchImages } = useLazyProfileImages();
  const profileImageBase64 = affAboId ? getProfileImage(affAboId) : undefined;
  const photoUrl =
    !row.isConfidential && profileImageBase64 ? `data:image/png;base64,${profileImageBase64}` : undefined;

  // WhatsApp number from independent lazy enrichment, when enabled for this report
  const { getWhatsAppNumber, getWhatsAppFetchState } = useLazyWhatsAppEnrichment({
    period,
    locale: storedLocale(),
    isoCountryCode: user.isoCountryCode || "US",
  });
  const whatsAppNumber = row.isConfidential
    ? undefined
    : enrichWhatsApp
      ? affAboId
        ? getWhatsAppNumber(affAboId)
        : undefined
      : (row.whatsapp ?? enrichedData?.whatsapp);
  const whatsAppLoading =
    !row.isConfidential && enrichWhatsApp && !isChina && !!affAboId && getWhatsAppFetchState(affAboId) === "fetching";

  // If enrichment provides imageRefURL and we haven't fetched the image yet, trigger fetch
  useEffect(() => {
    if (row.isConfidential || !affAboId || profileImageBase64) return;
    const enrichedImageRef = enrichedData?.imageRefURL;
    if (enrichedImageRef && !row.imageRefUrl) {
      fetchImages([{ ...row, imageRefUrl: enrichedImageRef }]);
    }
  }, [enrichedData?.imageRefURL, affAboId, profileImageBase64, row, fetchImages]);

  const isRowLoading =
    !isChina &&
    ((skipEnrichment === false && (fetchState === "fetching" || !enrichedData)) ||
      (awaitAutoEnrichment && !enrichedData));

  const kpiProps = selectedKpis.map((kpiId) => {
    const kpiConfig = fieldConfig.find(({ kpi }) => kpi === kpiId);
    const kpiValue = String(row[kpiId] ?? "");
    const resolvedValue = isChina ? (
      ""
    ) : isRowLoading ? (
      <LeafRectangleLoader isLoading width="100px" height="1rem" margin="0" />
    ) : kpiConfig?.renderValue ? (
      kpiConfig.renderValue(kpiValue, row)
    ) : (
      getKpiValue(
        kpiConfig?.id || kpiId,
        kpiValue,
        skipEnrichment,
        enrichedData || undefined,
        storedLocale(),
        row.businessNature,
      )
    );

    const isNoData = !isChina && !isRowLoading && resolvedValue === "--";

    return {
      title: kpiConfig?.title || "",
      value: resolvedValue,
      ariaLabel: isNoData ? noDataAvailableLabel : undefined,
      fullWidth: kpiConfig?.mobileFullWidth,
    };
  });

  const tags: {
    isProvisional?: boolean;
    isFirstContractYear?: boolean;
    businessNatureTag?: "abo" | "member" | "customer" | "guestCustomer";
    conversionType?: string;
    upForRenewal?: boolean;
    level?: number;
    businessNature?: string;
    showTagForDeepCustomers?: boolean;
    allowCustomerDetails?: boolean;
  } = {};
  if (row.isProvisional) tags["isProvisional"] = true;
  if (enrichedData?.isFirstContractYear === true) tags["isFirstContractYear"] = true;
  if (row.businessNatureTag)
    tags["businessNatureTag"] = row.businessNatureTag as "abo" | "member" | "customer" | "guestCustomer";
  if (row.conversionType) tags["conversionType"] = row.conversionType;
  if (row.upForRenewal === true) tags["upForRenewal"] = true;
  if (row.level != null) tags["level"] = row.level;
  if (row.businessNature != null) tags["businessNature"] = row.businessNature;
  if (showTagForDeepCustomers) tags["showTagForDeepCustomers"] = true;
  if (row.allowCustomerDetails) tags["allowCustomerDetails"] = true;

  const handleAboNameClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onProfileTrack();
    showABOProfile({
      detail: {
        abo: {
          affAbo: affAboId,
          isCustomer: row.isCustomer,
          isConfidential: row.isConfidential,
          level: row.level,
          allowCustomerDetails: row.allowCustomerDetails,
        },
        target: e.currentTarget,
      },
    });
    trackABONameClick();
  };

  const handleLineagePillClick = (clickedAffAbo: string) => {
    onProfileTrack();
    showABOProfile({
      detail: {
        abo: { affAbo: clickedAffAbo, isCustomer: false, isConfidential: false, level: undefined },
        target: undefined,
      },
    });
  };

  const onToggleWatchlist = async () => {
    const wasInWatchlist = isInWatchlist;
    const aboName =
      row.isConfidential || row.allowCustomerDetails
        ? row.aboName
        : enrichedData?.aboName?.trim()
          ? enrichedData.aboName
          : row.aboName;
    onWatchlistTrack(!isInWatchlist);
    try {
      await toggleWatchlist();
      onWatchlistNotification?.(aboName, wasInWatchlist, true);
    } catch {
      onWatchlistNotification?.(aboName, wasInWatchlist, false);
    }
  };

  const hideWatchlistButton =
    !enableWatchlist || (CUSTOMER_BUSINESS_NATURES.has(Number(row.businessNature)) && Number(row.level) >= 2);

  const isWatchlistReport = reportId === "watchlist";

  useEffect(() => {
    if (!isSelected || !ref.current) return;
    // See DesktopTable's TableRow: this correction stops useVirtuosoScrollToIndex's
    // remaining retries so they don't re-estimate and fight this scrollIntoView.
    // "end" (bottom-align) instead of "nearest" — see DesktopTable's TableRow for why.
    ref.current.scrollIntoView({ block: "end", behavior: "instant" });
    scrollClearOfStickyNav(ref.current);
    if (rowInViewRef) {
      rowInViewRef.current = { index: index, timestamp: Date.now() };
    }
  }, [index, isSelected, rowInViewRef]);

  return (
    <div ref={ref}>
      <MobileCard
        aboName={
          row.isConfidential || row.allowCustomerDetails
            ? row.aboName
            : enrichedData?.aboName?.trim()
              ? enrichedData.aboName
              : row.aboName
        }
        aboNumber={row.aboNumber}
        aff={row.aff}
        abo={row.aboNumber}
        aboOnclick={handleLineagePillClick}
        includeGlobalMB={isWatchlistReport}
        kpiRows={kpiProps}
        watchlist={!hideWatchlistButton ? isInWatchlist : undefined}
        toggleWatchlist={!hideWatchlistButton ? onToggleWatchlist : undefined}
        tags={tags}
        allowCustomerDetails={row.allowCustomerDetails}
        isInternational={row.isInternational}
        countryCode={row.countryCode}
        onAboNameClick={isChina ? undefined : handleAboNameClick}
        isLoading={isRowLoading}
        isChina={isChina}
        isSelected={isSelected}
        phoneNumber={row.isConfidential ? undefined : (row.phoneNumber ?? enrichedData?.phoneNumber)}
        onPhoneTrack={row.isConfidential ? undefined : onPhoneTrack}
        onMessageTrack={row.isConfidential ? undefined : onMessageTrack}
        whatsAppNumber={row.isConfidential ? undefined : whatsAppNumber}
        whatsAppLoading={row.isConfidential ? false : whatsAppLoading}
        onWhatsAppTrack={row.isConfidential ? undefined : onWhatsAppTrack}
        rankBadge={row.level}
        photoUrl={photoUrl}
        showImageBlock={showImageBlock}
        style={mobileCardStyle}
        isConfidential={row.isConfidential ?? false}
      />
    </div>
  );
};

export const MobileTable = ({
  rows,
  selectedKpis,
  enableWatchlist = false,
  reportId,
  period,
  batchSize,
  fieldConfig,
  showABOProfile,
  skipEnrichment = false,
  enrichWhatsApp = false,
  awaitAutoEnrichment = false,
  scrollToIndex,
  trackABONameClick = () => {},
  onProfileTrack,
  onWatchlistTrack,
  onPhoneTrack,
  onMessageTrack,
  onWhatsAppTrack,
  showImageBlock = false,
  mobileCardStyle = "standard",
  showTagForDeepCustomers = false,
}: ActionReportListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { virtuosoRef, rowInViewRef } = useVirtuosoScrollToIndex(scrollToIndex, containerRef);
  const noDataAvailableLabel = useNoDataAvailableLabel();
  const { t } = useTranslation(["actionReports", "watchlistNotifications"]);
  const [toastState, setToastState] = useState<"success" | "error">("success");
  const [showToast, setShowToast] = useState(false);
  const [toastName, setToastName] = useState("");
  const [toastWasInWatchlist, setToastWasInWatchlist] = useState(false);
  const [toastCounter, setToastCounter] = useState(0);

  const { kpisToFetch, onRowsRendered } = useActionReportList({
    rows,
    reportId,
    period,
    fieldConfig,
    batchSize,
    skipEnrichment,
    enrichWhatsApp,
  });

  const handleWatchlistNotification = useCallback((name: string, wasInWatchlist: boolean, success: boolean) => {
    setToastName(name);
    setToastWasInWatchlist(wasInWatchlist);
    setToastState(success ? "success" : "error");
    setToastCounter((prev) => prev + 1);
    setShowToast(true);
  }, []);

  const handleVisibleRowsChanged = useCallback(
    (range: { startIndex: number; endIndex: number }) => {
      onRowsRendered({ startIndex: range.startIndex, stopIndex: range.endIndex });
    },
    [onRowsRendered],
  );

  return (
    <>
      <LeafToastMessage
        showToast={showToast}
        theme={toastState}
        hideProgressBar={true}
        className="mini-notification"
        toastId={`watchlist-notification-${toastCounter}`}
        htmlContent={
          toastState === "success" ? (
            <span>
              {t(
                `watchlistNotifications:notification.${toastWasInWatchlist ? "removed" : "added"}`,
                toastWasInWatchlist ? watchlistContent.notification.removed : watchlistContent.notification.added,
                { name: toastName },
              )}
            </span>
          ) : (
            <span>{t("watchlistNotifications:notification.error", watchlistContent.notification.error)}</span>
          )
        }
      />
      <div ref={containerRef}>
        <Virtuoso
          ref={virtuosoRef}
          useWindowScroll
          data={rows}
          overscan={200}
          rangeChanged={handleVisibleRowsChanged}
          itemContent={(index, row) => (
            <MobileCardRow
              key={getAffAboId(row) || index}
              index={index}
              row={row}
              selectedKpis={selectedKpis}
              kpisToFetch={kpisToFetch}
              enableWatchlist={enableWatchlist}
              reportId={reportId}
              period={period}
              batchSize={batchSize}
              fieldConfig={fieldConfig}
              showABOProfile={showABOProfile}
              skipEnrichment={skipEnrichment}
              enrichWhatsApp={enrichWhatsApp}
              awaitAutoEnrichment={awaitAutoEnrichment}
              isSelected={index === scrollToIndex}
              rowInViewRef={rowInViewRef}
              noDataAvailableLabel={noDataAvailableLabel}
              trackABONameClick={trackABONameClick}
              onProfileTrack={onProfileTrack}
              onWatchlistTrack={onWatchlistTrack}
              onPhoneTrack={onPhoneTrack}
              onMessageTrack={onMessageTrack}
              onWhatsAppTrack={onWhatsAppTrack}
              showImageBlock={showImageBlock}
              mobileCardStyle={mobileCardStyle}
              showTagForDeepCustomers={showTagForDeepCustomers}
              onWatchlistNotification={handleWatchlistNotification}
            />
          )}
        />
      </div>
    </>
  );
};
